import { useState } from 'react';

import {
  Box,
  Grid,
  IconButton,
  List,
  ListItem,
  Paper,
} from '@material-ui/core';
import { alpha, makeStyles } from '@material-ui/core/styles';
import { Lock, VisibilityOutlined } from '@material-ui/icons';
import clsx from 'clsx';
import { useModal } from 'react-modal-hook';

import TagsV2 from 'components/TagsV2/TagsV2';
import VirtualizedListContainer from 'components/VirtualizedListContainer';
import { useVirtualizedList } from 'hooks/useVirtualizedList';
import dayjs from 'services/dayjs';
import { useAppDispatch } from 'store';
import {
  AtendimentoContatoStatusEnum,
  AtendimentoInterfaceV2,
  setHistoricoAtendimentoSelecionado,
} from 'store/reducers/atendimentos';
import getIconTipoCanal from 'utils/getIconTipoCanal';
import { normalizeDuration } from 'utils/normalizeDuration';

import DetailItemText from './DetailItemText';
import ModalHistoricoAtendimento from './ModalDetalhesAtendimento';

const useStyles = makeStyles(({ palette, spacing, shape }) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  scrollableList: {
    height: '100%',
    overflowY: 'auto',
    paddingRight: spacing(1),
    marginBottom: 32,
  },
  paper: {
    width: '100%',
    overflow: 'hidden',
    padding: spacing(0.5, 2),
    marginRight: spacing(1),
    backgroundColor: palette.background.paper,
  },
  paperSelected: {
    '&&': {
      backgroundColor: alpha(palette.primary.main, 0.1),
    },
  },
  lockIconContainer: {
    width: spacing(4.25),
    height: spacing(4.25),
    borderRadius: shape.borderRadius,
    backgroundColor: alpha(palette.primary.main, 0.1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

interface NewListaAtendimentosProps {
  atendimentos: AtendimentoInterfaceV2[];
  hasMore: boolean;
  isLoading: boolean;
  isFilter?: boolean;
  status: AtendimentoContatoStatusEnum;
  fetchNextAtendimentos: () => void;
}

export default function NewListaAtendimentos({
  atendimentos,
  hasMore,
  isLoading,
  isFilter = false,
  fetchNextAtendimentos,
  status,
}: NewListaAtendimentosProps) {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const [atendimentoSelected, setAtendimentoSelected] =
    useState<AtendimentoInterfaceV2>();
  const [selectedActionAtendimentoId, setSelectedActionAtendimentoId] =
    useState<string>();

  const { parentRef, rowVirtualizer, virtualItems } = useVirtualizedList({
    items: atendimentos,
    hasMore,
    estimateSize: 120,
    loading: isLoading,
    next: fetchNextAtendimentos,
  });

  // Seleciona o atendimento
  const handleSelectAtendimento = (atendimento: AtendimentoInterfaceV2) => {
    setSelectedActionAtendimentoId(currentId =>
      currentId === atendimento.id ? undefined : atendimento.id,
    );
    setAtendimentoSelected(atendimento);
    dispatch(setHistoricoAtendimentoSelecionado(atendimento));
    showModalHistoricoDeConversa();
  };

  const [showModalHistoricoDeConversa, hideModalHistoricoDeConversa] = useModal(
    ({ in: open }) => (
      <ModalHistoricoAtendimento
        open={open}
        onClose={hideModalHistoricoDeConversa}
        atendimento={atendimentoSelected as AtendimentoInterfaceV2}
      />
    ),
    [atendimentoSelected],
  );

  const renderAtendimentos = virtualItems.map(virtualRow => {
    const atendimento = atendimentos[virtualRow.index];

    if (!atendimento) return null;

    const {
      id,
      tags,
      code,
      startedIn,
      attendantFinisher,
      hasAccess,
      durationInSeconds,
      botFinisher,
      attendantCurrent,
      contact,
    } = atendimento;
    const dataInicio = dayjs(startedIn).format('DD/MM/YY [ás] HH:mm');
    const dataFim = dayjs(atendimento.finishedIn).format('DD/MM/YY [ás] HH:mm');
    const semAcesso = !hasAccess;
    const selected = selectedActionAtendimentoId === id;

    return (
      <ListItem
        key={id}
        disableGutters
        dense
        ref={rowVirtualizer.measureElement}
        data-index={virtualRow.index}
      >
        <Paper
          elevation={0}
          className={clsx(classes.paper, {
            [classes.paperSelected]: selected,
          })}
          onClick={() => setSelectedActionAtendimentoId(undefined)}
          style={{
            filter: semAcesso ? 'grayscale(100%)' : 'none',
            opacity: semAcesso ? 0.6 : 1,
          }}
        >
          <Grid container alignItems="center" wrap="nowrap" spacing={2}>
            <Grid item xs={3} zeroMinWidth>
              <DetailItemText label="Contato" value={contact?.name || ' - '} />
            </Grid>

            <Grid item xs={1} zeroMinWidth>
              <DetailItemText label="Código" value={code} />
            </Grid>

            <Grid item xs={3} zeroMinWidth>
              <DetailItemText label="Iniciado em" value={dataInicio} />
            </Grid>

            {status === AtendimentoContatoStatusEnum.FINISHED && (
              <Grid item xs={3} zeroMinWidth>
                <DetailItemText
                  label="Finalizado em"
                  value={atendimento?.finishedIn ? dataFim : '-'}
                />
              </Grid>
            )}

            <Grid item xs={2} zeroMinWidth>
              <DetailItemText
                label="Duração"
                value={normalizeDuration(durationInSeconds)}
              />
            </Grid>

            <Grid item xs={3} zeroMinWidth>
              <DetailItemText
                label={
                  status === AtendimentoContatoStatusEnum.FINISHED
                    ? 'Finalizado por'
                    : 'Atribuído a'
                }
                value={
                  status === AtendimentoContatoStatusEnum.FINISHED
                    ? attendantFinisher?.name || botFinisher?.name
                    : attendantCurrent?.name
                }
              />
            </Grid>

            <Grid item xs={5} zeroMinWidth>
              <DetailItemText
                label="Canal"
                value={
                  <Box
                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    <Box style={{ marginTop: 2 }}>
                      {atendimento.channel
                        ? getIconTipoCanal({
                            tipoCanal: atendimento.channel?.type,
                            size: 16,
                            isModoHibrido: atendimento.channel?.isHybridMode,
                          })
                        : ' - '}
                    </Box>

                    <Box
                      style={{
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={atendimento.channel?.name}
                    >
                      {atendimento.channel?.name}
                    </Box>
                  </Box>
                }
              />
            </Grid>

            <Grid
              item
              xs={3}
              style={{ display: 'flex', alignItems: 'center' }}
              zeroMinWidth
            >
              <TagsV2
                id={atendimento.id}
                tags={
                  tags?.map(tag => ({
                    id: tag.id,
                    cor: tag.color,
                    nome: tag.name,
                  })) || []
                }
              />
            </Grid>

            <Grid item xs>
              {semAcesso ? (
                <Box className={classes.lockIconContainer}>
                  <Lock color="primary" style={{ width: 16, height: 16 }} />
                </Box>
              ) : (
                <IconButton
                  size="small"
                  onClick={event => {
                    event.stopPropagation();
                    handleSelectAtendimento(atendimento);
                  }}
                >
                  <VisibilityOutlined />
                </IconButton>
              )}
            </Grid>
          </Grid>
        </Paper>
      </ListItem>
    );
  });

  return (
    <Box className={classes.root}>
      <List
        id="scrollableHistorico"
        className={classes.scrollableList}
        ref={parentRef}
      >
        <VirtualizedListContainer
          virtualItems={virtualItems}
          virtualizer={rowVirtualizer}
          loading={isLoading}
          empty={
            isFilter && atendimentos.length === 0
              ? {
                  title: 'Nenhum resultado',
                  description:
                    'Nenhum atendimento encontrado com base neste(s) filtro(s)!',
                }
              : {
                  title: 'Sem conversas',
                  description:
                    'Ainda não há histórico de conversas neste contato!',
                }
          }
        >
          {renderAtendimentos}
        </VirtualizedListContainer>
      </List>
    </Box>
  );
}
